import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Mail, Phone, MapPin, CheckCircle2 } from 'lucide-react';
import CalendlyInline from './CalendlyInline';

export default function Contact() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const perks = [
    'Free 30-minute consultation',
    'Get matched within 48–72 hours',
    'No contracts, no obligations',
  ];

  return (
    <section id="contact" className="py-28 md:py-40 bg-[#050A08] relative overflow-hidden" data-testid="contact-section">
      <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-emerald-500/[0.08] rounded-full blur-[150px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <span className="inline-block text-emerald-400 font-semibold text-sm uppercase tracking-[0.25em] mb-5">
              / Book a Call
            </span>
            <h2 className="font-display text-4xl md:text-6xl font-black text-white leading-[0.95] mb-6">
              Let&rsquo;s find your<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-lime-400">perfect match.</span>
            </h2>
            <p className="text-lg text-slate-400 leading-relaxed mb-10 max-w-md">
              Grab a time that works for you. On the call we&rsquo;ll learn about your workflow and pair you with a handpicked VA &mdash; no pressure, no commitments.
            </p>

            <ul className="space-y-4 mb-12">
              {perks.map((perk) => (
                <li key={perk} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <span className="text-slate-300">{perk}</span>
                </li>
              ))}
            </ul>

            <div className="space-y-6">
              {[
                { icon: Mail, label: 'Email', value: 'info@outsourcedhub.com' },
                { icon: Phone, label: 'Phone', value: '+1 562-352-3244' },
                { icon: MapPin, label: 'Location', value: 'Manila, Philippines' },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.label} className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">{item.label}</div>
                      <div className="text-white font-medium">{item.value}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-7"
          >
            <div className="bg-[#0D1612] rounded-3xl p-6 md:p-8 border border-white/[0.06]">
              <CalendlyInline />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
